/* eslint-disable prettier/prettier */
import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { Field, getIn } from 'formik';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { debounce, get } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import FormikAutocompleteSelect from '../Fields/FormikAutocompleteSelect';
import { COUNTRY_CITY_ENDPOINT } from '../../constants/endpointConstants';
import { getCitiesAction, resetCitiesAction, getList, getDynamicCountriesAction } from '../../containers/App/actions';
import {
  selectAggregationsLoading,
  selectCitiesOptions,
  selectDynamicCountriesOptions,
} from '../../containers/App/selectors';
import { getOption } from '../../utils/common';
import { emptySelectValue, TP, VISIBILITY } from '../../constants/consts';
import AddNewCountryModal from '../AddNewCountryModal';
import AddNewCityModal from '../AddNewCityModal';
import NoOptionsBox from '../NoOptionsBox';
import Button from '../Button';

const useStyles = makeStyles(theme => ({
  field: {
    width: '50%',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
}));

const getNoOptionProps = (isCreatable, t, buttonText, openFormFunc) =>
  isCreatable
    ? {
        customComponents: {
          NoOptionsMessage: () => (
            <NoOptionsBox buttonText={buttonText} openFormFunc={() => openFormFunc(true)} t={t} />
          ),
        },
      }
    : {};

const CountryCityBlock = ({
  countryField,
  cityField,
  countriesOptions,
  countryCities,
  formValues,
  setFieldValue,
  fieldClass,
  onCityChange,
  disabledFields,
  isCreatable,
  loadingFieldName,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [addNewCountryIsOpened, setAddNewCountryIsOpened] = React.useState(false);
  const [addNewCityIsOpened, setAddNewCityIsOpened] = React.useState(false);
  const [countryValue, setCountryValue] = React.useState('');
  const [cityValue, setCityValue] = React.useState('');
  const classes = useStyles();

  const getCities = (value, countryId) => {
    dispatch(
      getList({
        endpoint: COUNTRY_CITY_ENDPOINT(countryId),
        sagaRoutine: getCitiesAction,
        fieldName: cityField.name,
        params: {
          queryParams: { query: value, show_invalid: true, visibility: VISIBILITY.ALL },
        },
      }),
    );
  };

  const getCountries = debounce(v => {
    dispatch(getDynamicCountriesAction({ fieldName: countryField.name, params: { query: v } }));
  }, 300);

  const countryCreatableOptions = getNoOptionProps(
    isCreatable,
    t,
    `${TP}.FN_ADD_NEW_COUNTRY`,
    setAddNewCountryIsOpened,
  );

  const cityCreatableOptions = getNoOptionProps(isCreatable, t, `${TP}.FN_ADD_NEW_CITY`, setAddNewCityIsOpened);

  const onCityInputChange = (v = '') => {
    setCityValue(v);
    const country = getIn(formValues, countryField.name);
    if (country?.value) {
      getCities(v, country?.value);
    }
  };

  const onCountryInputChange = (v = '') => {
    setCountryValue(v);
    getCountries(v);
  };

  const onCountryChange = () => {
    setFieldValue(cityField.name, null);
    dispatch(resetCitiesAction());
  };

  return (
    <>
      <Grid item className={cn(classes.field, fieldClass)}>
        <Field
          name={countryField.name}
          component={FormikAutocompleteSelect}
          instanceId={countryField.name}
          label={t(countryField.label)}
          options={countriesOptions}
          onChange={onCountryChange}
          isClearable
          isEditable
          withFiltering
          isDisabled={disabledFields?.country}
          isLoading={loadingFieldName === countryField.name}
          {...countryCreatableOptions}
          onInputTextChange={onCountryInputChange}
          staticOption={
            isCreatable && <Button onClick={setAddNewCountryIsOpened}>{t(`${TP}.FN_ADD_NEW_COUNTRY`)}</Button>
          }
        />
      </Grid>
      <Grid item className={cn(classes.field, fieldClass)}>
        <Field
          name={cityField.name}
          component={FormikAutocompleteSelect}
          instanceId={cityField.name}
          label={t(cityField.label)}
          isDisabled={!getIn(formValues, `${countryField?.name}.value`) || disabledFields?.city}
          options={countryCities.options}
          isLoading={loadingFieldName === cityField.name}
          onChange={onCityChange}
          isClearable
          isEditable
          onFocus={onCityInputChange}
          {...cityCreatableOptions}
          onInputTextChange={onCityInputChange}
          staticOption={isCreatable && <Button onClick={setAddNewCityIsOpened}>{t(`${TP}.FN_ADD_NEW_CITY`)}</Button>}
        />
      </Grid>
      {addNewCountryIsOpened && (
        <AddNewCountryModal
          countryValue={countryValue}
          onClose={country => {
            if (country.id) {
              setFieldValue(countryField.name, getOption(country));
            }
            setAddNewCountryIsOpened(false);
          }}
        />
      )}
      {addNewCityIsOpened && (
        <AddNewCityModal
          countriesOptions={countriesOptions}
          onClose={city => {
            if (city.id) {
              setFieldValue(countryField.name, getOption(city?.country));
              setFieldValue(cityField.name, getOption(city));
            }
            setAddNewCityIsOpened(false);
          }}
          countryValue={get(formValues, countryField.name)}
          cityValue={cityValue}
        />
      )}
    </>
  );
};

CountryCityBlock.propTypes = {
  countryField: PropTypes.object,
  cityField: PropTypes.object,
  countriesOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  formValues: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
  onCityChange: PropTypes.func,
  disabledFields: PropTypes.shape({
    country: PropTypes.bool,
    city: PropTypes.bool,
  }),
  isCreatable: PropTypes.bool,
  loadingFieldName: PropTypes.string,
};

CountryCityBlock.defaultProps = {
  countryField: { name: 'country', label: `${TP}.m_COUNTRY` },
  cityField: { name: 'city', label: `${TP}.m_CITY` },
  countriesOptions: [emptySelectValue],
  formValues: null,
  onCityChange: () => {},
  disabledFields: {
    country: false,
    city: false,
  },
  isCreatable: false,
  loadingFieldName: 'city',
};

const mapStateToProps = createStructuredSelector({
  countryCities: selectCitiesOptions(),
  loadingFieldName: selectAggregationsLoading(),
  countriesOptions: selectDynamicCountriesOptions(),
});

export default connect(mapStateToProps)(CountryCityBlock);
